import {ApolloQueryResult, FetchResult, gql} from '@apollo/client';
import {customerClient, uploadingCustomerClient} from './graphql_clients';
import {
    AddVehicleMutation,
    CustomerDataInput,
    DeleteAccountMutation,
    DeleteVehicleMutation,
    GetChatQuery,
    GetMeQuery,
    GetNotificationsQuery,
    MarkMessagesReadMutation,
    MarkNotificationReadMutation,
    OnboardingCompletedMutation,
    ProfilePictureMutation,
    SendChatMessageMutation,
    SetDefaultLocationMutation,
    SyncCustomerDataMutation,
    UpdatePreferencesMutation,
    EditVehicleMutation,
    GetRegisteredDevicesQuery,
} from '../gql/customer/graphql';
import {CustomerPreferencesInput} from '../gql/pwa/graphql.ts';

const VEHICLE_FRAGMENT = gql`
    fragment VehicleFragment on Vehicle {
        id
        brand
        model
        identifiers {
            type
            value
            __typename
        }
        buildyear
        lease
        mileage
        tires
        color
        fuel_type
        inspection_expiration_date
        images {
            type
            url
            __typename
        }
        __typename
    }
`;

const CUSTOMER_PREFERENCES_FRAGMENT = gql`
    fragment CustomerPreferencesFragment on CustomerPreferences {
        receive_marketing_messages
        receive_service_messages
        receive_personal_messages
        receive_general_messages
        receive_email_channel
        receive_phone_channel
        receive_whatsapp_channel
        receive_sms_channel
        receive_mobile_phone_channel
        receive_mail_channel
        __typename
    }
`;

const CUSTOMER_FRAGMENT = gql`
    fragment CustomerFragment on Customer {
        id
        default_location_id
        default_specialty_id
        data {
            name
            key
            value
            __typename
        }
        profilePicture {
            signed_url
            __typename
        }
        preferences {
            ...CustomerPreferencesFragment
            __typename
        }
        vehicles {
            ...VehicleFragment
            mileageHistory {
                mileage
                date
                is_from_maintenance
                __typename
            }
            __typename
        }
        __typename
    }
    ${CUSTOMER_PREFERENCES_FRAGMENT}
    ${VEHICLE_FRAGMENT}
`;

const GET_ME = gql`
    query getMe {
        me {
            id
            email
            customer {
                ...CustomerFragment
                __typename
            }
            onboarding_completed
            show_onboarding
            __typename
        }
        customerFields {
            key
            name
            default
            classification
            show_public
            edit_public
            __typename
        }
    }
    ${CUSTOMER_FRAGMENT}
`;

const NOTIFICATION_FRAGMENT = gql`
    fragment NotificationFragment on NotificationType {
        id
        title
        message
        action
        read
        created_at
        created_at_iso8601
        updated_at
        updated_at_iso8601
        type
        vehicle_id
        emailSettings {
            header_image_url
            footer_image_url
            logo_image_url
            background_colour
            text_colour
            button_colour
            button_text_colour
            footer_background_colour
            footer_text_colour
            phone_number
            email_address
            website
            address
            socialMedia {
                facebook
                instagram
                linkedin
                snapchat
                tiktok
                twitter
                youtube
            }
        }
        icon
        icon_colour_background
        message_title
        message_title_colour
        __typename
    }
`;

const CHAT_MESSAGE_FRAGMENT = gql`
    fragment ChatMessageFragment on ChatMessageType {
        id
        action
        message
        file {
            signed_url
            original_filename
            mime_type
            __typename
        }
        user {
            id
            name
            token
            __typename
        }
        persistedNotification {
            ...NotificationFragment
            __typename
        }
        read
        created_at
        created_at_iso8601
        __typename
    }

    ${NOTIFICATION_FRAGMENT}
`;

const CHAT_FRAGMENT = gql`
    fragment ChatFragment on ChatType {
        id
        uuid
        messages(read: $read, last_message_id: $last_message_id) {
            ...ChatMessageFragment
            __typename
        }
        __typename
    }
    ${CHAT_MESSAGE_FRAGMENT}
`;

const GET_CHAT = gql`
    query getChat($last_message_id: Int, $read: Boolean) {
        chat {
            ...ChatFragment
            __typename
        }
    }
    ${CHAT_FRAGMENT}
`;

const GET_NOTIFICATIONS = gql`
    query getNotifications {
        notifications {
            ...NotificationFragment
            __typename
        }
    }
    ${NOTIFICATION_FRAGMENT}
`;

const MARK_NOTIFICATION_READ = gql`
    mutation MarkNotificationRead($id: Int!) {
        readNotification(id: $id)
    }
`;

const MARK_MESSAGES_READ = gql`
    mutation markMessagesRead {
        markMessagesRead
    }
`;

const SEND_CHAT_MESSAGE = gql`
    mutation sendChatMessage(
        $message: String!
        $url: String
        $persisted_notification_id: Int
        $last_message_id: Int
        $read: Boolean
        $file: Upload
    ) {
        SendChatMessage(
            message: $message
            url: $url
            persisted_notification_id: $persisted_notification_id
            file: $file
        ) {
            ...ChatFragment
            __typename
        }
    }
    ${CHAT_FRAGMENT}
`;

const SYNC_CUSTOMER_DATA = gql`
    mutation SyncCustomerData($data: [CustomerDataInput]!) {
        customer: SyncCustomerData(customerData: $data) {
            ...CustomerFragment
            __typename
        }
    }

    ${CUSTOMER_FRAGMENT}
`;
const UPDATE_PREFERENCES_DATA = gql`
    mutation UpdatePreferences($data: CustomerPreferencesInput) {
        customer: UpdatePreferences(customerPreferences: $data) {
            ...CustomerPreferencesFragment
            __typename
        }
    }

    ${CUSTOMER_PREFERENCES_FRAGMENT}
`;

const SEND_DEFAULT_LOCATION = gql`
    mutation setDefaultLocation($id: Int!) {
        SetDefaultLocation(id: $id)
    }
`;

const SEND_PROFILE_PICTURE = gql`
    mutation profilePicture($profilePicture: Upload!) {
        ProfilePicture(profile_picture: $profilePicture) {
            profilePicture {
                signed_url
                __typename
            }
            __typename
        }
    }
`;

const REGISTER_CUSTOMER_DEVICE = gql`
    mutation registerCustomerDevice($firebase_token: String, $device_type: DeviceEnum) {
        RegisterCustomerDevice(firebase_token: $firebase_token, device_type: $device_type) {
            id
            __typename
        }
    }
`;

const GET_REGISTERED_DEVICES = gql`
    query getRegisteredDevices {
        me {
            customerDevices {
                firebase_token
                __typename
            }
            __typename
        }
    }
`;

const DELETE_ACCOUNT_MUTATION = gql`
    mutation deleteAccount {
        deleteAccount
    }
`;

const ONBOARDING_COMPLETED_MUTATION = gql`
    mutation onboardingCompleted {
        onboardingCompleted
    }
`;

const ADD_VEHICLE_MUTATION = gql`
    mutation addVehicle($license_plate: String) {
        AddVehicle(vehicle_identifier: $license_plate) {
            ...VehicleFragment
            __typename
        }
    }
    ${VEHICLE_FRAGMENT}
`;

const EDIT_VEHICLE_MUTATION = gql`
    mutation EditVehicle($vehicle: VehicleInput!) {
        UpdateVehicle(vehicle: $vehicle) {
            ...VehicleFragment
            __typename
        }
    }
    ${VEHICLE_FRAGMENT}
`;

const DELETE_VEHICLE_MUTATION = gql`
    mutation deleteVehicle($id: Int) {
        RemoveVehicle(id: $id)
    }
`;

export class Customer {
    static async me(token: string): Promise<ApolloQueryResult<GetMeQuery>> {
        const result = await customerClient.query<GetMeQuery>({
            query: GET_ME,
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async chat(token: string, read?: boolean, lastMessageId?: number): Promise<ApolloQueryResult<GetChatQuery>> {
        const result = await customerClient.query<GetChatQuery>({
            query: GET_CHAT,
            fetchPolicy: 'no-cache',
            variables: {
                read,
                last_message_id: lastMessageId,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async sendChatMessage(
        token: string,
        message: string,
        url?: string,
        persistedNotificationId?: number,
        lastMessageId?: number,
        file?: File | FormDataEntryValue | null,
    ): Promise<FetchResult<SendChatMessageMutation>> {
        const result = await uploadingCustomerClient.mutate<SendChatMessageMutation>({
            mutation: SEND_CHAT_MESSAGE,
            variables: {
                message,
                url,
                persisted_notification_id: persistedNotificationId,
                last_message_id: lastMessageId,
                file: file,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async markMessagesRead(token: string): Promise<FetchResult<MarkMessagesReadMutation>> {
        const result = await customerClient.mutate<MarkMessagesReadMutation>({
            mutation: MARK_MESSAGES_READ,
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async notifications(token: string): Promise<ApolloQueryResult<GetNotificationsQuery>> {
        const result = await customerClient.query<GetNotificationsQuery>({
            query: GET_NOTIFICATIONS,
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });
        return result;
    }

    static async markNotificationRead(token: string, notificationId: number) {
        const result = await customerClient.mutate<MarkNotificationReadMutation>({
            mutation: MARK_NOTIFICATION_READ,
            variables: {
                id: notificationId,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });
        return result;
    }

    static async sendProfileData(
        token: string,
        data: CustomerDataInput[],
    ): Promise<FetchResult<SyncCustomerDataMutation>> {
        const result = await customerClient.mutate<SyncCustomerDataMutation>({
            mutation: SYNC_CUSTOMER_DATA,
            variables: {
                data,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async sendPreferencesData(
        token: string,
        data: CustomerPreferencesInput,
    ): Promise<FetchResult<UpdatePreferencesMutation>> {
        const result = await customerClient.mutate<UpdatePreferencesMutation>({
            mutation: UPDATE_PREFERENCES_DATA,
            variables: {
                data,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async sendDefaultLocation(token: string, locationId: number) {
        const result = await customerClient.mutate<SetDefaultLocationMutation>({
            mutation: SEND_DEFAULT_LOCATION,
            variables: {
                id: locationId,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async sendProfilePicture(token: string, imageFile: File) {
        await uploadingCustomerClient.mutate<ProfilePictureMutation>({
            mutation: SEND_PROFILE_PICTURE,
            variables: {
                profilePicture: imageFile,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        // ProfilePictureMutation result has null for profilePicture for some reason
        await customerClient.clearStore();
        return await this.me(token);
    }

    static async registerCustomerDevice(token: string, firebaseToken: string, device: string) {
        await uploadingCustomerClient.mutate<ProfilePictureMutation>({
            mutation: REGISTER_CUSTOMER_DEVICE,
            variables: {
                firebase_token: firebaseToken,
                device_type: device,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });
    }

    static async registeredDevices(token: string): Promise<ApolloQueryResult<GetRegisteredDevicesQuery>> {
        const result = await customerClient.query<GetRegisteredDevicesQuery>({
            query: GET_REGISTERED_DEVICES,
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async markOnboardingCompleted(token: string) {
        const result = await customerClient.mutate<OnboardingCompletedMutation>({
            mutation: ONBOARDING_COMPLETED_MUTATION,
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async deleteAccount(token: string) {
        const result = await customerClient.mutate<DeleteAccountMutation>({
            mutation: DELETE_ACCOUNT_MUTATION,
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }
    static async addVehicle(license_plate: string, token: string) {
        const result = await customerClient.mutate<AddVehicleMutation>({
            mutation: ADD_VEHICLE_MUTATION,
            variables: {
                license_plate: license_plate,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }

    static async editVehicle(id: number, tires: string, lease: boolean, mileage: number, token: string) {
        const result = await customerClient.mutate<EditVehicleMutation>({
            mutation: EDIT_VEHICLE_MUTATION,
            variables: {
                vehicle: {
                    id: id,
                    tires: tires,
                    lease: lease,
                    mileage: mileage,
                },
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }
    static async deleteVehicle(id: number, token: string) {
        const result = await customerClient.mutate<DeleteVehicleMutation>({
            mutation: DELETE_VEHICLE_MUTATION,
            variables: {
                id: id,
            },
            context: {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        });

        return result;
    }
}
